// Considered: ChipListDisplayAtom — rejected because it is display only: no action, no empty-state
// copy, no read-only reason and no first-use hint. Chips here are ChipAtom, one per Archetype, so
// primary and unavailable states carry through. See spec Reuse Audit.
import React, { useRef, useState } from 'react';
import { Toast } from 'primereact/toast';
import { useTranslation } from 'localization';
import ChipAtom from '../../atoms/Chip/ChipAtom';
import ButtonAtom from '../../atoms/Button/ButtonAtom';
import SkeletonAtom from '../../atoms/Skeleton/SkeletonAtom';
import MessageAtom from '../../atoms/Message/MessageAtom';
import ArchetypeMultiPickerMolecule from '../ArchetypeMultiPicker/ArchetypeMultiPickerMolecule';
import type { UseSphereArchetypesResult } from '../../../hooks/useSphereArchetypes';
import type { ArchetypeSelection } from '../../../api/sphereArchetypesApi';
import './ArchetypesFieldMolecule.scss';

const HINT_SEEN_KEY = 'center.archetypes.hintSeen';

function readHintSeen(): boolean {
  try {
    return globalThis.localStorage?.getItem(HINT_SEEN_KEY) === '1';
  } catch {
    return false;
  }
}

function writeHintSeen(): void {
  try {
    globalThis.localStorage?.setItem(HINT_SEEN_KEY, '1');
  } catch {
    // Storage can be unavailable (private mode); the hint then simply stays visible.
  }
}

export interface ArchetypesFieldMoleculeProps {
  /** The hook's result for this sphere — the field renders and drives it, it owns no data. */
  archetypes: UseSphereArchetypesResult;
  /** Who can change the set ("Only {name} can change this"), when the caller knows; generic copy otherwise. */
  readOnlyOwnerName?: string;
  className?: string;
}

const ArchetypesFieldMolecule: React.FC<ArchetypesFieldMoleculeProps> = ({
  archetypes: state,
  readOnlyOwnerName,
  className = '',
}) => {
  const { t } = useTranslation();
  const toastRef = useRef<Toast>(null);
  const [pickerOpen, setPickerOpen] = useState(false);
  const [saveError, setSaveError] = useState(false);
  const [forbidden, setForbidden] = useState(false);
  const [hintSeen, setHintSeen] = useState(readHintSeen);
  const [hintOpen, setHintOpen] = useState(false);
  const labelId = React.useId();

  const openPicker = () => {
    state.clearFeedback();
    setSaveError(false);
    setForbidden(false);
    void state.loadAvailable();
    setPickerOpen(true);
  };

  const showUpdatedToast = (previous: ArchetypeSelection) => {
    toastRef.current?.show({
      severity: 'success',
      life: 8000,
      content: (
        <output className="archetypes-field__toast">
          <span>{t('archetypes.toast.updated')}</span>
          <ButtonAtom
            variant="text"
            label={t('archetypes.toast.undo')}
            onClick={async () => {
              toastRef.current?.clear();
              const undone = await state.save(previous);
              if (undone === 'saved') {
                toastRef.current?.show({ severity: 'info', life: 4000, summary: t('archetypes.toast.restored') });
              }
            }}
          />
        </output>
      ),
    });
  };

  // Schema-default entries (origin: 'schema-default') are never a PUT target — the server
  // rejects one as `is_schema_default` — so every read of the assignable set drops them first.
  const assignedOnly = state.archetypes.filter((a) => a.origin !== 'schema-default');

  const handleSave = async (selection: ArchetypeSelection) => {
    const previous: ArchetypeSelection = {
      archetypes: assignedOnly.map((a) => a.systemName),
      primary: assignedOnly.find((a) => a.isPrimary)?.systemName ?? null,
    };
    setSaveError(false);
    setForbidden(false);
    const outcome = await state.save(selection);
    if (outcome === 'saved') {
      setPickerOpen(false);
      if (!hintSeen) {
        writeHintSeen();
        setHintSeen(true);
      }
      showUpdatedToast(previous);
    } else if (outcome === 'error') {
      setSaveError(true);
    } else if (outcome === 'forbidden') {
      setForbidden(true);
    }
  };

  const showHint = !hintSeen || hintOpen;
  const isEmpty = state.status === 'ready' && state.archetypes.length === 0;

  let body: React.ReactNode;
  if (state.status === 'loading') {
    body = (
      <div className="archetypes-field__loading" data-testid="archetypes-field-loading" aria-busy="true">
        <SkeletonAtom count={3} width="6rem" height="1.75rem" gap="var(--spacing-xs)" />
      </div>
    );
  } else if (state.status === 'error') {
    body = (
      <MessageAtom
        severity="error"
        content={
          <div className="archetypes-field__error">
            <span>{t('archetypes.field.loadError')}</span>
            <ButtonAtom variant="secondary" label={t('archetypes.field.retry')} onClick={state.refetch} />
          </div>
        }
      />
    );
  } else if (isEmpty) {
    // Inline copy, not `EmptyStateAtom`: that atom is a centred 3xl-padded block with a 48px icon,
    // right for the picker's empty list and far too loud for one field in the editor panel. The
    // state is still complete — it says what is missing and the Change button below it is the way
    // out (ux-craft S1).
    body = (
      <p className="archetypes-field__empty" data-testid="archetypes-field-empty">
        {t('archetypes.field.empty')}
      </p>
    );
  } else {
    body = (
      <ul className="archetypes-field__chips" aria-label={t('archetypes.field.listLabel')}>
        {state.archetypes.map((a) => {
          const unavailable = !a.resolved;
          const inherited = a.origin === 'schema-default';
          let name = a.displayName;
          if (unavailable) name = t('archetypes.chip.unavailableName', { name: a.displayName });
          else if (inherited) name = t('archetypes.chip.inheritedName', { name: a.displayName });
          else if (a.isPrimary) name = t('archetypes.chip.primaryName', { name: a.displayName });
          return (
            <li
              key={`${a.systemName}-${a.origin}`}
              className={`archetypes-field__chip${a.isPrimary ? ' archetypes-field__chip--primary' : ''}`}
              aria-label={name}
              title={name}
            >
              <ChipAtom
                label={a.displayName}
                unavailable={unavailable}
                unavailableReason={t('archetypes.chip.unavailable')}
                locked={inherited}
                lockedReason={t('archetypes.chip.inherited')}
              />
            </li>
          );
        })}
      </ul>
    );
  }

  const canChange = state.status !== 'loading' && state.status !== 'error' && state.canAssign;
  // The server could not determine the authority: `canAssign` is false, but that is not a denial,
  // so the field neither offers Change nor says who can change it (spec D47, XR1).
  const authorityUnavailable = state.status === 'ready' && !state.canAssign && state.assignGateUnavailable;
  const readOnly = state.status === 'ready' && !state.canAssign && !state.assignGateUnavailable;
  let dataState: string = state.status;
  if (authorityUnavailable) dataState = 'authority-unavailable';
  else if (readOnly) dataState = 'read-only';

  return (
    <section className={`archetypes-field ${className}`.trim()} aria-labelledby={labelId} data-state={dataState}>
      <div className="archetypes-field__head">
        <span id={labelId} className="archetypes-field__label">
          {t('archetypes.field.label')}
        </span>
        {hintSeen && (
          <button
            type="button"
            className="archetypes-field__info pi pi-info-circle"
            aria-label={t('archetypes.field.infoLabel')}
            aria-expanded={hintOpen}
            onClick={() => setHintOpen((open) => !open)}
          />
        )}
      </div>
      {body}
      {showHint && state.status === 'ready' && <p className="archetypes-field__hint">{t('archetypes.field.hint')}</p>}
      {canChange && (
        <ButtonAtom variant="secondary" size="small" label={t('archetypes.field.change')} onClick={openPicker} />
      )}
      {authorityUnavailable && (
        <MessageAtom
          severity="warn"
          content={
            <div className="archetypes-field__unavailable" data-testid="archetypes-field-authority-unavailable">
              <span>{t('archetypes.field.authorityUnavailable')}</span>
              <ButtonAtom
                variant="secondary"
                size="small"
                label={t('archetypes.field.retry')}
                loading={state.refreshing}
                onClick={state.refetch}
              />
            </div>
          }
        />
      )}
      {readOnly && (
        <p className="archetypes-field__read-only">
          {readOnlyOwnerName ? t('archetypes.field.readOnlyBy', { name: readOnlyOwnerName }) : t('archetypes.field.readOnly')}
        </p>
      )}
      <ArchetypeMultiPickerMolecule
        visible={pickerOpen}
        assigned={assignedOnly}
        available={state.available}
        status={state.availableStatus === 'idle' ? 'loading' : (state.availableStatus as 'loading' | 'ready' | 'error')}
        problems={state.problems}
        conflict={state.conflict}
        saveError={saveError}
        forbidden={forbidden}
        saving={state.saving}
        onSave={handleSave}
        onCancel={() => setPickerOpen(false)}
        onRetry={() => void state.loadAvailable()}
      />
      <Toast ref={toastRef} position="bottom-right" />
    </section>
  );
};

export default ArchetypesFieldMolecule;
